import { Box, IconButton, Button, Tooltip, Chip, Select, MenuItem, FormControl, InputLabel, Avatar, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Alert, Snackbar, Autocomplete, Typography, CircularProgress } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import TextField from '@mui/material/TextField'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { getAllContent, getAllTags, getAllStylesForFilter, getAllSectionsForFilter } from '../../graphql/queries'
import { deleteMultipleContent, reorderContent } from '../../graphql/mutation'
import { useState, useEffect, useRef } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ImageIcon from '@mui/icons-material/Image'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import DeleteIcon from '@mui/icons-material/Delete'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'

export function DetailPage({ id }) {
  const navigate = useNavigate()

  const handleNavigate = async () => {
    navigate(`/content/${id}`)
  }
  return (
    <Tooltip title="View Content Details">
      <Button
        onClick={handleNavigate}
        variant="outlined"
        className="actionBtn"
        sx={{ fontSize: {xs:'0.7rem', md:'small'} }}
      >
        Details
      </Button>
    </Tooltip>
  )
}

export function CopyCell({ value }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton size="small" onClick={handleCopy}>
        <ContentCopyIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  )
}

// Row component for drag and drop
function ContentRow({ item, index, isDragEnabled, isSelected, onSelect }) {
  const navigate = useNavigate()

  const getChipColor = (type) => {
    switch (type) {
      case 'PROMPT': return 'primary'
      case 'STYLE': return 'secondary'
      case 'SECTION': return 'success'
      default: return 'default'
    }
  }

  const renderRelatedEntity = () => {
    const { section, style, prompt } = item
    if (section) {
      return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip label="Section" color="success" size="small" variant="outlined" />
          <span>{section.name}</span>
        </Box>
      )
    }
    if (style) {
      return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip label="Style" color="secondary" size="small" variant="outlined" />
          <span>{style.name}</span>
        </Box>
      )
    }
    if (prompt) {
      return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip label="Prompt" color="primary" size="small" variant="outlined" />
          <span>{prompt.name || 'Unnamed Prompt'}</span>
        </Box>
      )
    }
    return <span style={{ color: '#999', fontStyle: 'italic' }}>No relation</span>
  }

  return (
    <Draggable draggableId={item.id} index={index} isDragDisabled={!isDragEnabled}>
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0',
            bgcolor: snapshot.isDragging ? '#e3f2fd' : isSelected ? '#f5f5f5' : 'white',
            '&:hover': {
              bgcolor: snapshot.isDragging ? '#e3f2fd' : '#fafafa',
            },
            transition: 'background-color 0.15s ease',
          }}
        >
          {/* Checkbox */}
          <Box sx={{ width: 50, p: 1.5, display: 'flex', justifyContent: 'center' }}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onSelect(item.id)}
              style={{ width: 18, height: 18, cursor: 'pointer' }}
            />
          </Box>

          {/* Drag Handle + Row Number */}
          <Box sx={{ width: 120, p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            {isDragEnabled && (
              <Box
                {...provided.dragHandleProps}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'grab',
                  color: '#9e9e9e',
                  '&:hover': { color: '#616161' },
                  '&:active': { cursor: 'grabbing' },
                }}
              >
                <DragIndicatorIcon fontSize="small" />
              </Box>
            )}
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {index + 1}
            </Typography>
            <DetailPage id={item.id} />
          </Box>

          {/* Content Type */}
          <Box sx={{ width: 130, p: 1.5 }}>
            <Chip 
              label={item.contentType} 
              color={getChipColor(item.contentType)}
              size="small"
              variant="outlined"
            />
          </Box>

          {/* Banner */}
          <Box sx={{ width: 80, p: 1.5, display: 'flex', justifyContent: 'center' }}>
            {item.banner ? (
              <Avatar 
                src={item.banner} 
                variant="rounded"
                sx={{ width: 32, height: 32 }}
              >
                <ImageIcon />
              </Avatar>
            ) : (
              <Box sx={{ color: '#999' }}>
                <ImageIcon fontSize="small" />
              </Box>
            )}
          </Box>

          {/* Video */}
          <Box sx={{ width: 80, p: 1.5, display: 'flex', justifyContent: 'center' }}>
            {item.videoUrl ? (
              <Tooltip title="Has video">
                <VideoLibraryIcon color="primary" fontSize="small" />
              </Tooltip>
            ) : (
              <Box sx={{ color: '#999' }}>
                <VideoLibraryIcon fontSize="small" />
              </Box>
            )}
          </Box>

          {/* GIF */}
          <Box sx={{ width: 80, p: 1.5, display: 'flex', justifyContent: 'center' }}>
            {item.gifUrl ? (
              <Tooltip title="Has GIF">
                <ImageIcon color="secondary" fontSize="small" />
              </Tooltip>
            ) : (
              <Box sx={{ color: '#999' }}>
                <ImageIcon fontSize="small" />
              </Box>
            )}
          </Box>

          {/* Related Entity */}
          <Box sx={{ flex: 1, minWidth: 200, p: 1.5 }}>
            {renderRelatedEntity()}
          </Box>

          {/* Tag */}
          <Box sx={{ width: 150, p: 1.5 }}>
            {item.tag ? (
              <Chip 
                label={item.tag.name} 
                color="info"
                size="small"
                variant="outlined"
              />
            ) : (
              <span style={{ color: '#999', fontStyle: 'italic' }}>No tag</span>
            )}
          </Box>

          {/* Content ID */}
          <Box sx={{ width: 220, p: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box sx={{ 
                maxWidth: '160px', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.875rem',
              }}>
                {item.id}
              </Box>
              <CopyCell value={item.id} />
            </Box>
          </Box>
        </Box>
      )}
    </Draggable>
  )
}

export default function ContentTable() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialSearch = searchParams.get('search') || ''
  const initialContentType = searchParams.get('contentType') || ''
  const initialDesignType = searchParams.get('designType') || ''
  const initialTag = searchParams.get('tag') || ''
  const initialSection = searchParams.get('section') || ''
  const initialStyle = searchParams.get('style') || ''
  const [search, setSearch] = useState(initialSearch)
  const [contentTypeFilter, setContentTypeFilter] = useState(initialContentType)
  const [designTypeFilter, setDesignTypeFilter] = useState(initialDesignType)
  const [tagFilter, setTagFilter] = useState(initialTag)
  const [sectionFilter, setSectionFilter] = useState(initialSection)
  const [styleFilter, setStyleFilter] = useState(initialStyle)
  const [selectedRows, setSelectedRows] = useState([])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState('success')
  const [localRows, setLocalRows] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const searchRef = useRef(null)

  const { loading, error, data, refetch } = useQuery(getAllContent, {
    variables: {
      tagId: tagFilter || null,
      sectionId: sectionFilter || null,
      styleId: styleFilter || null
    }
  })
  const { data: tagsData } = useQuery(getAllTags)
  const { data: stylesData } = useQuery(getAllStylesForFilter)
  const { data: sectionsData } = useQuery(getAllSectionsForFilter)
  const [deleteMultipleContentMutation, { loading: deleteLoading }] = useMutation(deleteMultipleContent)
  const [reorderContentMutation, { loading: reorderLoading }] = useMutation(reorderContent)

  // Check if any relational filter is active (enables drag-and-drop)
  const hasRelationalFilter = Boolean(tagFilter || sectionFilter || styleFilter)

  useEffect(() => {
    const params = {}
    if (search) params.search = search
    if (contentTypeFilter) params.contentType = contentTypeFilter
    if (designTypeFilter) params.designType = designTypeFilter
    if (tagFilter) params.tag = tagFilter
    if (sectionFilter) params.section = sectionFilter
    if (styleFilter) params.style = styleFilter
    setSearchParams(params)
  }, [search, contentTypeFilter, designTypeFilter, tagFilter, sectionFilter, styleFilter, setSearchParams])

  // Update local rows when data changes
  useEffect(() => {
    if (data?.getAllContent) {
      let filteredContent = [...data.getAllContent]

      if (search) {
        filteredContent = filteredContent.filter(item =>
          (item.contentId && item.contentId.toLowerCase().includes(search.toLowerCase())) ||
          item.id.toLowerCase().includes(search.toLowerCase()) ||
          (item.tag && item.tag.name.toLowerCase().includes(search.toLowerCase())) ||
          (item.section && item.section.name.toLowerCase().includes(search.toLowerCase())) ||
          (item.style && item.style.name.toLowerCase().includes(search.toLowerCase())) ||
          (item.prompt && item.prompt.name && item.prompt.name.toLowerCase().includes(search.toLowerCase()))
        )
      }

      if (contentTypeFilter) {
        filteredContent = filteredContent.filter(item => item.contentType === contentTypeFilter)
      }

      if (designTypeFilter) {
        filteredContent = filteredContent.filter(item => item.designType === designTypeFilter)
      }

      // Sort by rowNumber if filter is active, otherwise by creation date
      if (hasRelationalFilter) {
        filteredContent.sort((a, b) => (a.rowNumber || 999) - (b.rowNumber || 999))
      } else {
        filteredContent.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      }

      setLocalRows(filteredContent)
    }
  }, [data, search, contentTypeFilter, designTypeFilter, hasRelationalFilter])

  const handleSearchChange = (event) => {
    setSearch(event.target.value)
  }

  const handleClearSearch = () => {
    setSearch('')
    if (searchRef.current) {
      searchRef.current.focus()
    }
  }

  const handleContentTypeChange = (event) => {
    setContentTypeFilter(event.target.value)
  }

  const handleTagChange = (event, newValue) => {
    setTagFilter(newValue ? newValue.id : '')
  }

  const handleSectionChange = (event, newValue) => {
    setSectionFilter(newValue ? newValue.id : '')
  }

  const handleStyleChange = (event, newValue) => {
    setStyleFilter(newValue ? newValue.id : '')
  }

  const handleRowSelect = (id) => {
    setSelectedRows(prev => 
      prev.includes(id) 
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id]
    )
  }

  const handleSelectAll = () => {
    if (selectedRows.length === localRows.length) {
      setSelectedRows([])
    } else {
      setSelectedRows(localRows.map(row => row.id))
    }
  }

  const handleDeleteClick = () => {
    if (selectedRows.length > 0) {
      setDeleteDialogOpen(true)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
  }

  const handleDeleteConfirm = async () => {
    try {
      await deleteMultipleContentMutation({
        variables: { ids: selectedRows }
      })
      setSnackbarMessage(`Successfully deleted ${selectedRows.length} content item(s)`)
      setSnackbarSeverity('success')
      setSnackbarOpen(true)
      setSelectedRows([])
      setDeleteDialogOpen(false)
      refetch()
    } catch (error) {
      setSnackbarMessage(`Error deleting content: ${error.message}`)
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
      setDeleteDialogOpen(false)
    }
  }

  const handleSnackbarClose = () => {
    setSnackbarOpen(false)
  }

  const handleDragEnd = async (result) => {
    if (!result.destination || result.source.index === result.destination.index) {
      return
    }

    const items = Array.from(localRows)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    // Update local state immediately for responsive UI
    setLocalRows(items)

    // Prepare updates for the backend
    const updates = items.map((item, index) => ({
      id: item.id,
      rowNumber: index + 1
    }))

    try {
      await reorderContentMutation({
        variables: { updates }
      })
      setSnackbarMessage('Order updated successfully')
      setSnackbarSeverity('success')
      setSnackbarOpen(true)
    } catch (error) {
      // Revert on error
      refetch()
      setSnackbarMessage(`Error updating order: ${error.message}`)
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    }
  }

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
  if (error) return <Box sx={{ p: 4, color: 'error.main' }}>Error: {error.message}</Box>

  const paginatedRows = localRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  const totalPages = Math.ceil(localRows.length / rowsPerPage)

  return (
    <Box sx={{ width: '100%' }}>
      {/* Delete Button */}
      {selectedRows.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            disabled={deleteLoading}
          >
            Delete {selectedRows.length} Selected
          </Button>
        </Box>
      )}

      {/* Filters */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        mb: 2, 
        flexWrap: 'wrap',
        alignItems: 'center' 
      }}>
        {/* Search */}
        <TextField
          ref={searchRef}
          label="Search"
          variant="outlined"
          size="small"
          value={search}
          onChange={handleSearchChange}
          placeholder="Search by content ID, entity name, tag..."
          sx={{ minWidth: 250 }}
          InputProps={{
            endAdornment: search && (
              <IconButton size="small" onClick={handleClearSearch}>
                <CloseIcon fontSize="small" />
              </IconButton>
            ),
          }}
        />

        {/* Content Type Filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Content Type</InputLabel>
          <Select
            value={contentTypeFilter}
            label="Content Type"
            onChange={handleContentTypeChange}
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="PROMPT">Prompt</MenuItem>
            <MenuItem value="STYLE">Style</MenuItem>
            <MenuItem value="SECTION">Section</MenuItem>
          </Select>
        </FormControl>

          {/* Tag Filter */}
        <Autocomplete
          size="small"
          sx={{ minWidth: 150 }}
          options={tagsData?.getAllTags || []}
          getOptionLabel={(option) => option.name}
          value={tagsData?.getAllTags.find(tag => tag.id === tagFilter) || null}
          onChange={handleTagChange}
          renderInput={(params) => <TextField {...params} label="Tag" />}
          clearOnEscape
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />

        {/* Section Filter */}
        <Autocomplete
          size="small"
          sx={{ minWidth: 150 }}
          options={sectionsData?.getAllSections || []}
          getOptionLabel={(option) => option.name}
          value={sectionsData?.getAllSections.find(section => section.id === sectionFilter) || null}
          onChange={handleSectionChange}
          renderInput={(params) => <TextField {...params} label="Section" />}
          clearOnEscape
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />

        {/* Style Filter */}
        <Autocomplete
          size="small"
          sx={{ minWidth: 150 }}
          options={stylesData?.getAllStylesAdmin?.styles || []}
          getOptionLabel={(option) => option.name}
          value={stylesData?.getAllStylesAdmin?.styles.find(style => style.id === styleFilter) || null}
          onChange={handleStyleChange}
          renderInput={(params) => <TextField {...params} label="Style" />}
          clearOnEscape
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
      </Box>
        
      {/* Drag hint */}
      {hasRelationalFilter && (
        <Box sx={{ 
          mb: 2, 
          p: 1.5, 
          bgcolor: '#e3f2fd', 
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <DragIndicatorIcon fontSize="small" sx={{ color: '#1976d2' }} />
          <Typography variant="body2" color="primary">
            Drag rows to reorder content. Changes are saved automatically.
          </Typography>
          {reorderLoading && <CircularProgress size={16} />}
        </Box>
      )}

      {/* Table Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '2px solid #e0e0e0',
          bgcolor: '#fafafa',
          fontWeight: 600,
          fontSize: '0.875rem',
        }}
      >
        <Box sx={{ width: 50, p: 1.5, display: 'flex', justifyContent: 'center' }}>
          <input
            type="checkbox"
            checked={selectedRows.length === localRows.length && localRows.length > 0}
            onChange={handleSelectAll}
            style={{ width: 18, height: 18, cursor: 'pointer' }}
          />
        </Box>
        <Box sx={{ width: 120, p: 1.5 }}>#</Box>
        <Box sx={{ width: 130, p: 1.5 }}>Content Type</Box>
        <Box sx={{ width: 80, p: 1.5, textAlign: 'center' }}>Banner</Box>
        <Box sx={{ width: 80, p: 1.5, textAlign: 'center' }}>Video</Box>
        <Box sx={{ width: 80, p: 1.5, textAlign: 'center' }}>GIF</Box>
        <Box sx={{ flex: 1, minWidth: 200, p: 1.5 }}>Related Entity</Box>
        <Box sx={{ width: 150, p: 1.5 }}>Tag</Box>
        <Box sx={{ width: 220, p: 1.5 }}>Content ID</Box>
      </Box>

      {/* Table Body with Drag and Drop */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="content-list">
          {(provided) => (
            <Box
              {...provided.droppableProps}
              ref={provided.innerRef}
              sx={{ minHeight: 200 }}
            >
              {paginatedRows.map((item, index) => (
                <ContentRow
                  key={item.id}
                  item={item}
                  index={index}
                  isDragEnabled={hasRelationalFilter}
                  isSelected={selectedRows.includes(item.id)}
                  onSelect={handleRowSelect}
                />
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      {/* Pagination */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        alignItems: 'center', 
        p: 2,
        gap: 2,
        borderTop: '1px solid #e0e0e0',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">Rows per page:</Typography>
          <Select
            size="small"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(e.target.value)
              setPage(0)
            }}
            sx={{ minWidth: 70 }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </Box>
        <Typography variant="body2">
          {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, localRows.length)} of {localRows.length}
        </Typography>
        <Box>
          <IconButton 
            size="small" 
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
          >
            {'<'}
          </IconButton>
          <IconButton 
            size="small" 
            disabled={page >= totalPages - 1}
            onClick={() => setPage(p => p + 1)}
          >
            {'>'}
          </IconButton>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete {selectedRows.length} selected content item(s)? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}
